import { Check, ChevronDown, Search } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";

import { cn } from "@/lib/utils";

// ── Types ────────────────────────────────────────────────────────────────────

export interface SelectOption {
  [key: string]: any;
}

interface SearchableSelectProps<T extends SelectOption> {
  /** The list of options to display */
  options: T[] | Record<string, string> | null | undefined;
  /** Key used to display the option label (supports dot-notation, e.g. "fields.summary") */
  labelKey?: string;
  /** Key used as the unique value identifier */
  valueKey?: string;
  /** Currently selected value (controlled) – matches against valueKey */
  value?: string | number | null;
  /** Callback when an option is selected or cleared */
  onValueChange: (option: T | undefined) => void;
  /** Enable the search/filter input inside the dropdown */
  searchable?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  className?: string;
  /** Custom renderer for each option row */
  renderOption?: (option: T) => React.ReactNode;
  /** Custom renderer for the selected value in the trigger button */
  renderValue?: (option: T) => React.ReactNode;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function getNestedValue(obj: any, path: string): any {
  return path.split(".").reduce((acc, key) => acc?.[key], obj);
}

function isSameVal(a: any, b: any): boolean {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  return String(a) === String(b);
}

// ── Component ────────────────────────────────────────────────────────────────

export function SearchableSelect<T extends SelectOption>({
  options,
  labelKey = "name",
  valueKey = "id",
  value,
  onValueChange,
  searchable = false,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  disabled = false,
  className,
  renderOption,
  renderValue,
}: SearchableSelectProps<T>) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const [internalValue, setInternalValue] = useState<string | number | null>(
    null,
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Controlled vs uncontrolled: use prop when provided, otherwise internal state
  const isControlled = value !== undefined;
  const currentValue = isControlled ? value : internalValue;

  // Normalize options into a uniform shape
  const normalizedOptions = useMemo(() => {
    if (!options) return [];

    if (Array.isArray(options)) {
      if (options.length === 0) return [];
      if (typeof options[0] === "string" || typeof options[0] === "number") {
        return (options as unknown as (string | number)[]).map((val) => ({
          item: { [labelKey]: String(val), [valueKey]: val } as unknown as T,
          label: String(val),
          val,
        }));
      }
      return options.map((item) => ({
        item,
        label: String(getNestedValue(item, labelKey) ?? ""),
        val: getNestedValue(item, valueKey) ?? "",
      }));
    }

    // Record<string, string>
    return Object.entries(options).map(([label, val]) => ({
      item: { [labelKey]: label, [valueKey]: val } as unknown as T,
      label,
      val,
    }));
  }, [options, labelKey, valueKey]);

  // Filter by search query
  const filteredOptions = useMemo(() => {
    if (!searchable || !search.trim()) return normalizedOptions;
    const q = search.toLowerCase();
    return normalizedOptions.filter((opt) =>
      opt.label.toLowerCase().includes(q),
    );
  }, [normalizedOptions, search, searchable]);

  // Find the currently selected option
  const selectedOption = useMemo(() => {
    if (currentValue == null) return undefined;
    return normalizedOptions.find((opt) => isSameVal(opt.val, currentValue));
  }, [currentValue, normalizedOptions]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Position & focus on open
  useEffect(() => {
    if (open) {
      const btn = buttonRef.current;
      if (btn) {
        const rect = btn.getBoundingClientRect();
        const dropdownHeight = Math.min(
          filteredOptions.length * 40 + (searchable ? 44 : 0),
          284,
        );
        const spaceBelow = window.innerHeight - rect.bottom;
        const openUpward = spaceBelow < dropdownHeight + 8;
        setDropdownStyle({
          position: "fixed",
          top: openUpward ? rect.top - dropdownHeight - 4 : rect.bottom + 4,
          left: rect.left,
          width: rect.width,
          zIndex: 1000000,
          pointerEvents: "auto",
        });
      }
      if (searchable) {
        setTimeout(() => inputRef.current?.focus(), 0);
      }
    }
  }, [open, filteredOptions.length, searchable]);

  // Resolve the portal container: prefer the Radix portal div (direct child of body,
  // no CSS transform, not inert) so position:fixed coords are viewport-relative and
  // Radix Dialog's focus-trap doesn't block pointer events.
  const getPortalContainer = (): Element => {
    const portalRoot = containerRef.current?.closest("[data-radix-portal]");
    return portalRoot ?? document.body;
  };

  const handleSelect = (option: { item: T; val: string | number }) => {
    // Toggle selection: clicking the same item deselects
    if (selectedOption && isSameVal(selectedOption.val, option.val)) {
      if (!isControlled) setInternalValue(null);
      onValueChange(undefined);
    } else {
      if (!isControlled) setInternalValue(option.val);
      onValueChange(option.item);
    }
    setOpen(false);
    setSearch("");
  };

  return (
    <div className={cn("relative w-full", className)} ref={containerRef}>
      {/* Trigger button */}
      <button
        type="button"
        ref={buttonRef}
        disabled={disabled}
        onMouseDown={(e) => {
          // Prevent focus-trap interference when inside a Radix Dialog modal.
          e.preventDefault();
          setOpen((prev) => !prev);
        }}
        className={cn(
          "flex min-h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-1.5 text-[12.5px]",
          "focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/20",
          "disabled:cursor-not-allowed disabled:opacity-50",
        )}
      >
        <span
          className={cn(
            "min-w-0 flex-1 truncate text-left",
            selectedOption ? "text-foreground" : "text-muted-foreground",
          )}
        >
          {selectedOption
            ? renderValue
              ? renderValue(selectedOption.item)
              : renderOption
                ? renderOption(selectedOption.item)
                : selectedOption.label
            : placeholder}
        </span>
        <ChevronDown size={16} className="ml-1 shrink-0 opacity-50" />
      </button>

      {/* Dropdown panel */}
      {open &&
        ReactDOM.createPortal(
          <div
            ref={dropdownRef}
            style={dropdownStyle}
            className="rounded-md border bg-popover shadow-md"
          >
            {/* Search input (conditional) */}
            {searchable && (
              <div className="flex items-center gap-2 border-b px-3 py-2">
                <Search size={14} className="shrink-0 text-muted-foreground" />
                <input
                  ref={inputRef}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="flex-1 bg-transparent text-[12.5px] outline-none placeholder:text-muted-foreground"
                />
              </div>
            )}

            {/* Options list */}
            <div
              className="max-h-56 overflow-y-auto py-1"
              onWheel={(e) => e.stopPropagation()}
            >
              {filteredOptions.length === 0 ? (
                <p className="px-3 py-4 text-center text-[12.5px] text-muted-foreground">
                  No options found.
                </p>
              ) : (
                filteredOptions.map((option) => {
                  const isSelected = selectedOption
                    ? isSameVal(selectedOption.val, option.val)
                    : false;
                  return (
                    <button
                      key={String(option.val)}
                      type="button"
                      onMouseDown={(e) => {
                        // Prevent focus-trap from moving focus back to the dialog
                        // before our selection handler fires (Radix Dialog modal fix).
                        e.preventDefault();
                        handleSelect(option);
                      }}
                      className={cn(
                        "flex w-full items-center justify-between px-3 py-1.5 text-left text-[12.5px] hover:bg-accent",
                        isSelected && "bg-accent/50 font-normal",
                      )}
                    >
                      <span className="truncate">
                        {renderOption
                          ? renderOption(option.item)
                          : option.label}
                      </span>
                      {isSelected && (
                        <Check
                          size={14}
                          className="ml-2 shrink-0 text-primary"
                        />
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>,
          getPortalContainer(),
        )}
    </div>
  );
}
